import React, { useEffect, useRef, useState } from 'react';
import { NOT_REQUIRED_MESSAGE, readinessSkipsOf } from './readinessSkips';
import { formatMoneyDisplay } from '../../utils/currencyUtils';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  getBillingCycleEoePreview,
  getBillingCycleHippsPreview,
  getBillingCycleReadiness,
  getAuthorizationErrors,
  listEpisodeAuthorizations,
  updateBillingWorksheet,
  updateOrder,
} from '../../services/api';
import type {
  AuthorizationBudget,
  AuthorizationError,
  AuthorizationResponse,
  BillableStatus,
  EoePreviewOrderItem,
  EoePreviewResponse,
  GetBillingCyclesResponse,
  OasisVisitDetail,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { invalidDiagnosisCodeClaimMessage } from '../../utils/diagnosisCodeValidity';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { getLockedBillingRequirementMessage } from '../../utils/orderBillingRequirement';
import { Link } from 'react-router-dom';
import {
  billingOrderPath,
  patientAuthorizationsPath,
  patientEpisodePath,
  patientVisitPath,
} from '../../utils/navigation';
import { scrollToField } from '../../utils/scrollToField';
import { ImportedFromExternalEmrMark } from '../task/ImportedFromExternalEmrMark';
import {
  buildInsuranceIdFields,
  usesPatientInsuranceId,
} from './worksheet/insuranceIdFields';
import {
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
} from '../task/taskStatus';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { FeatureFlag, PermissionSlug } from '../../constants/featureFlags';
import { useNavigateWithNewTab } from '../../hooks/useNavigateWithNewTab';
import { NewTabIconLink } from './NewTabIconLink';
import { PhoneLink } from '../PhoneLink';
import {
  EXTERNAL_COMPUTED_HIPPS_MESSAGE,
  EXTERNAL_DEFAULT_HIPPS_MESSAGE,
  EXTERNAL_MANUAL_HIPPS_LABEL,
  PAYMENT_OASIS_EXTERNAL_MESSAGE,
  PAYMENT_OASIS_MISSING_MESSAGE,
} from '../../constants/billing';
import type { DraftReadinessScope } from './ClaimReadinessProvider';
import { ClaimReadinessDetails } from './ClaimReadinessDetails';

export interface EOEPreviewPopupProps {
  billingCycle: GetBillingCyclesResponse | null;
  onOpenChange: (open: boolean) => void;
  /** Draft cycles on the Ready tab use `true`; Not Ready uses `false`. */
  isDraftReady?: boolean;
  /**
   * The caller's current draft-list scope. Present only for draft tabs: it adds
   * the small field-correction area backed by the cycle readiness-details read.
   * Omitted (every submitted caller) keeps the old behavior with no new request
   * or link.
   */
  readinessScope?: DraftReadinessScope;
  /**
   * Called after an order's billing requirement is saved. The change can move
   * this claim between the Ready and Not Ready lists, so the lists refetch.
   */
  onBillingDataChanged?: () => void;
}

// Hidden for now at Pedram's request — flip to re-enable the F2F check row.
const SHOW_F2F_CHECK = false;

// 'warn' is shown but does not count as blocking.
type CheckStatus = 'pass' | 'fail' | 'warn';

// A refresh that finds the popup closed, or showing another cycle, is skipped
// rather than failed: there is nothing on screen for it to update.
type PreviewRefreshResult = 'refreshed' | 'skipped' | 'failed';

interface CheckItem {
  key: string;
  label: string;
  status: CheckStatus;
  message: string;
  anchor?: string;
  /**
   * Optional second line under the message. The HIPPS row uses it to name
   * provenance (submission default or biller-entered) without changing the
   * pass/fail verdict.
   */
  note?: string;
  noteTone?: 'warning' | 'muted';
  /** One line per finding, listed under the message. */
  details?: string[];
  /**
   * Show the message in the muted tone even though the status is 'warn':
   * a check that could not run, which is not a finding.
   */
  mutedMessage?: boolean;
}

interface FieldDef {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  span2?: boolean;
  anchor?: string;
}

// The six discipline budget fields tracked by authorization checks (matches AuthorizationsTab).
const AUTH_DISCIPLINE_FIELDS: {
  key: keyof AuthorizationResponse;
  label: string;
}[] = [
  { key: 'sn_visits', label: 'SN' },
  { key: 'pt_visits', label: 'PT' },
  { key: 'ot_visits', label: 'OT' },
  { key: 'st_visits', label: 'ST' },
  { key: 'msw_visits', label: 'MSW' },
  { key: 'hha_visits', label: 'HHA' },
];

/** Unset/unknown statuses read as BILLABLE, matching the claim worksheet. */
function visitBillableStatusValue(
  status: string | null | undefined
): BillableStatus {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'NON_BILLABLE') return 'NON_BILLABLE';
  if (s === 'NON_COVERED') return 'NON_COVERED';
  return 'BILLABLE';
}

const EOEPreviewPopup: React.FC<EOEPreviewPopupProps> = ({
  billingCycle: billingCycleProp,
  onOpenChange,
  readinessScope,
  onBillingDataChanged,
}) => {
  const [preview, setPreview] = useState<EoePreviewResponse | null>(null);
  // The cycle's own readiness row, refetched after an in-place edit here
  // changes it. The list row this popup opened from is not
  // refetched, so its flags would otherwise go stale until reopened.
  const [refreshedCycle, setRefreshedCycle] =
    useState<GetBillingCyclesResponse | null>(null);
  const billingCycle =
    refreshedCycle &&
    refreshedCycle.billing_cycle_id === billingCycleProp?.billing_cycle_id
      ? refreshedCycle
      : billingCycleProp;
  const [hippsCode, setHippsCode] = useState<string | null>(null);
  // Server-supplied display copy naming the OASIS item or diagnosis to fix.
  // Set only for refusals a biller can act on; null otherwise.
  const [hippsUnavailableReason, setHippsUnavailableReason] = useState<
    string | null
  >(null);
  // submission-only provenance for the previewed HIPPS. Set when the
  // claim is anchored on externally billed history; null for a normal code.
  const [hippsSource, setHippsSource] = useState<
    'external_default' | 'external_computed' | 'external_manual' | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<{
    auths: AuthorizationResponse[];
    errors: AuthorizationError[];
    // Server-computed used/allowed per active auth and discipline, so the
    // popup never re-derives which visits consume a slot.
    budget: AuthorizationBudget[];
  } | null>(null);
  const [authLoadFailed, setAuthLoadFailed] = useState(false);
  // Orders with a billing-requirement save in flight.
  const [savingOrderIds, setSavingOrderIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // Visits with a billable-status save in flight.
  const [savingVisitIds, setSavingVisitIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // The billing cycle on screen now. Read after an await, so a save that
  // finishes once the popup has closed or moved on cannot write its preview.
  const displayedCycleIdRef = useRef<string | null>(null);
  useEffect(() => {
    displayedCycleIdRef.current = billingCycleProp?.billing_cycle_id ?? null;
  }, [billingCycleProp]);

  const { error: showError } = useToast();

  // Refetches only the preview after a billing-requirement save. HIPPS does
  // not depend on the requirement, and the dialog keeps showing the current
  // preview instead of dropping back to its loading state.
  const refreshPreview = async (
    billingCycleId: string
  ): Promise<PreviewRefreshResult> => {
    const isStillDisplayed = () =>
      displayedCycleIdRef.current === billingCycleId;
    if (!isStillDisplayed()) return 'skipped';
    try {
      const response = await getBillingCycleEoePreview({
        path: { billing_cycle_id: billingCycleId },
      });
      if (!isStillDisplayed()) return 'skipped';
      if (response?.error || !response?.data) {
        console.error('Failed to refresh EOE preview:', response?.error);
        return 'failed';
      }
      setPreview(response.data);
      return 'refreshed';
    } catch (err) {
      console.error('Failed to refresh EOE preview:', err);
      return isStillDisplayed() ? 'failed' : 'skipped';
    }
  };

  const refreshReadiness = async (
    billingCycleId: string
  ): Promise<PreviewRefreshResult> => {
    const isStillDisplayed = () =>
      displayedCycleIdRef.current === billingCycleId;
    if (!isStillDisplayed()) return 'skipped';
    try {
      const response = await getBillingCycleReadiness({
        path: { billing_cycle_id: billingCycleId },
      });
      if (!isStillDisplayed()) return 'skipped';
      if (response?.error || !response?.data) {
        console.error(
          'Failed to refresh billing cycle readiness:',
          response?.error
        );
        return 'failed';
      }
      setRefreshedCycle(response.data);
      return 'refreshed';
    } catch (err) {
      console.error('Failed to refresh billing cycle readiness:', err);
      return isStillDisplayed() ? 'failed' : 'skipped';
    }
  };

  // Shared by the load effect and the post-save refresh. Resolves null on any
  // failure: the generated client resolves HTTP failures as { error } rather
  // than rejecting, so a failed fetch must not read as empty-but-successful.
  const fetchAuthData = async (cycle: {
    episode_id: string;
    patient_id: string;
  }): Promise<NonNullable<typeof authData> | null> => {
    try {
      const [a, e] = await Promise.all([
        listEpisodeAuthorizations({
          path: { episode_id: cycle.episode_id },
        }),
        getAuthorizationErrors({
          path: { episode_id: cycle.episode_id },
        }),
      ]);
      if (a.error || e.error) {
        console.error(
          'Failed to load authorizations for claim preview',
          a.error ?? e.error
        );
        return null;
      }
      return {
        auths: a.data ?? [],
        errors: e.data?.errors ?? [],
        budget: e.data?.budget ?? [],
      };
    } catch (err) {
      console.error('Failed to load authorizations for claim preview', err);
      return null;
    }
  };

  const refreshAuthData = async (
    cycle: GetBillingCyclesResponse
  ): Promise<PreviewRefreshResult> => {
    if (!cycle.is_authorization_required || !cycle.episode_id) return 'skipped';
    const isStillDisplayed = () =>
      displayedCycleIdRef.current === cycle.billing_cycle_id;
    const data = await fetchAuthData({
      episode_id: cycle.episode_id,
      patient_id: cycle.patient_id,
    });
    if (!isStillDisplayed()) return 'skipped';
    if (data === null) return 'failed';
    setAuthData(data);
    return 'refreshed';
  };

  const handleRequiredForBillingChange = async (
    orderId: string,
    requiredForBilling: boolean
  ) => {
    if (!billingCycle) return;
    const billingCycleId = billingCycle.billing_cycle_id;
    setSavingOrderIds(prev => new Set(prev).add(orderId));
    try {
      let saved = false;
      try {
        const result = await updateOrder({
          path: { order_id: orderId },
          body: { required_for_billing: requiredForBilling },
        });
        // The generated client resolves HTTP failures as { error } rather
        // than rejecting.
        if (result?.error) {
          console.error('Failed to update billing requirement:', result.error);
        } else {
          saved = true;
        }
      } catch (err) {
        console.error('Failed to update billing requirement:', err);
      }
      if (!saved) {
        showError(
          'Update Failed',
          'Unable to update billing requirement. Please try again.'
        );
        return;
      }
      onBillingDataChanged?.();
      if ((await refreshPreview(billingCycleId)) === 'failed') {
        showError(
          'Preview Not Refreshed',
          'Saved, but the preview could not refresh. Reopen it to see the latest.'
        );
      }
    } finally {
      setSavingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  // The same write the claim worksheet's "Billable status" select
  // makes, saved per row on change since the popup has no Save button. A
  // non-billable visit stops consuming an authorization slot, so the
  // authorization data and the cycle's readiness row are refetched along
  // with the preview.
  const handleBillableStatusChange = async (
    visitId: string,
    status: BillableStatus
  ) => {
    if (!billingCycle) return;
    const billingCycleId = billingCycle.billing_cycle_id;
    setSavingVisitIds(prev => new Set(prev).add(visitId));
    try {
      let saved = false;
      try {
        const result = await updateBillingWorksheet({
          body: {
            billing_cycle_id: billingCycleId,
            visit_updates: [{ visit_id: visitId, new_billing_status: status }],
          },
        });
        if (result?.error) {
          console.error('Failed to update billable status:', result.error);
        } else {
          saved = true;
        }
      } catch (err) {
        console.error('Failed to update billable status:', err);
      }
      if (!saved) {
        showError(
          'Update Failed',
          'Unable to update billable status. Please try again.'
        );
        return;
      }
      onBillingDataChanged?.();
      const results = await Promise.all([
        refreshPreview(billingCycleId),
        refreshReadiness(billingCycleId),
        refreshAuthData(billingCycle),
      ]);
      if (results.includes('failed')) {
        showError(
          'Preview Not Refreshed',
          'Saved, but the preview could not refresh. Reopen it to see the latest.'
        );
      }
    } finally {
      setSavingVisitIds(prev => {
        const next = new Set(prev);
        next.delete(visitId);
        return next;
      });
    }
  };

  const navigateTo = useNavigateWithNewTab();
  const { hasPermission, hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const canViewOrders = hasPermission(PermissionSlug.ORDER_MANAGER);
  // Relaxed billing readiness — mirrors the backend
  // PENDING_SIGNATURE_BILLING_READINESS gate used by the readiness checks.
  const pendingSignatureReady = hasFeatureFlag(
    FeatureFlag.PENDING_SIGNATURE_BILLING_READINESS
  );

  // "View authorizations" only: plain click navigates + closes the dialog;
  // Cmd/Ctrl+click opens a new tab and leaves it open (useNavigateWithNewTab).
  // Row links open in a new tab and never close the dialog.
  const navigateAndClose = (path: string) => (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey) {
      onOpenChange(false);
    }
    navigateTo(path)(e);
  };

  const renderRowLinkCell = (path: string, label: string) => (
    <td className='py-1.5 pl-2 text-right'>
      <NewTabIconLink href={path} label={label} />
    </td>
  );

  useEffect(() => {
    setRefreshedCycle(null);
    if (!billingCycleProp) {
      setPreview(null);
      setHippsCode(null);
      setHippsSource(null);
      setHippsUnavailableReason(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [previewResponse, hippsResponse] = await Promise.all([
          getBillingCycleEoePreview({
            path: { billing_cycle_id: billingCycleProp.billing_cycle_id },
          }),
          getBillingCycleHippsPreview({
            path: { billing_cycle_id: billingCycleProp.billing_cycle_id },
          }),
        ]);
        if (cancelled) return;
        // The generated client resolves HTTP failures as { error } rather than
        // rejecting. Without this check a failed request would read as "no
        // HIPPS code" — a claim-readiness verdict — instead of a load failure.
        if (previewResponse?.error || hippsResponse?.error) {
          console.error(
            'Failed to load EOE preview:',
            previewResponse?.error ?? hippsResponse?.error
          );
          setPreview(null);
          setHippsCode(null);
          setHippsSource(null);
          setHippsUnavailableReason(null);
          setError('Unable to load billing cycle details.');
          return;
        }
        setPreview(previewResponse?.data ?? null);
        setHippsCode(hippsResponse?.data?.hipps_code ?? null);
        setHippsSource(hippsResponse?.data?.hipps_source ?? null);
        setHippsUnavailableReason(
          hippsResponse?.data?.unavailable_reason ?? null
        );
      } catch (err) {
        console.error('Failed to load EOE preview:', err);
        if (cancelled) return;
        setPreview(null);
        setHippsCode(null);
        setHippsSource(null);
        setHippsUnavailableReason(null);
        setError('Unable to load billing cycle details.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingCycleProp]);

  const authEpisodeId = billingCycleProp?.episode_id ?? null;
  const authPatientId = billingCycleProp?.patient_id ?? null;
  const authRequired = billingCycleProp?.is_authorization_required ?? false;
  useEffect(() => {
    if (!authRequired || !authEpisodeId || !authPatientId) {
      setAuthData(null);
      setAuthLoadFailed(false);
      return;
    }
    let cancelled = false;
    setAuthLoadFailed(false);
    // Clear prior cycle's data so switching cycles shows loading, not stale auths.
    setAuthData(null);
    (async () => {
      const data = await fetchAuthData({
        episode_id: authEpisodeId,
        patient_id: authPatientId,
      });
      if (cancelled) return;
      if (data === null) {
        setAuthLoadFailed(true);
        return;
      }
      setAuthData(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [authEpisodeId, authPatientId, authRequired]);

  const getMiddleInitial = (middleName?: string | null): string =>
    middleName?.trim()?.charAt(0)?.toUpperCase() || '-';

  const displayValue = (value?: string | null): string =>
    value && value.trim() !== '' ? value : '-';

  const displayDate = (value?: string | null): string =>
    formatDateToMMDDYYYY(value) || '-';

  const renderVisitTaskLabel = (task: string, trailing?: React.ReactNode) => (
    <span className='inline-flex flex-wrap items-center gap-1.5'>
      <span>{task}</span>
      {trailing}
    </span>
  );

  const renderVisitStatusCell = (status: string, isImported?: boolean) => (
    <span className='inline-flex items-center gap-1.5'>
      {renderStatusBadge(status)}
      <ImportedFromExternalEmrMark imported={isImported} />
    </span>
  );

  const statusLabel = (s: string) =>
    STATUS_DISPLAY_LABELS[s] ??
    s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const renderStatusBadge = (s: string) => (
    <Badge variant={getStatusBadgeVariant(s as never)}>{statusLabel(s)}</Badge>
  );

  const demographics = preview?.patient_demographics;
  const address = preview?.patient_address;
  const physician = preview?.physician_information;
  const episodeInfo = preview?.episode_information;
  const admissionInfo = preview?.admission_information;

  const patientFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(demographics?.last_name) },
    { label: 'First name', value: displayValue(demographics?.first_name) },
    {
      label: 'Middle initial',
      value: getMiddleInitial(demographics?.middle_name),
    },
    { label: 'Gender', value: displayValue(demographics?.gender) },
    {
      label: 'Date of birth',
      value: displayDate(demographics?.date_of_birth),
    },
    ...buildInsuranceIdFields(demographics, 'eoe-mbi'),
    {
      label: 'Medical record number',
      value: displayValue(demographics?.mrn),
      mono: true,
    },
    {
      label: 'Phone number',
      value: <PhoneLink phone={demographics?.phone} fallback='-' />,
    },
    { label: 'Address', value: displayValue(address?.address), span2: true },
    { label: 'City', value: displayValue(address?.city) },
    { label: 'State', value: displayValue(address?.state) },
    { label: 'Zip', value: displayValue(address?.zip_code) },
  ];

  const physicianFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(physician?.last_name) },
    { label: 'First name', value: displayValue(physician?.first_name) },
    { label: 'Physician NPI', value: displayValue(physician?.npi), mono: true },
    { label: 'PECOS status', value: displayValue(physician?.pecos_status) },
  ];

  const episodeAdmissionFields: FieldDef[] = [
    { label: 'Type of bill', value: displayValue(episodeInfo?.type_of_bill) },
    {
      label: 'Start of care date',
      value: displayDate(episodeInfo?.soc_date),
    },
    {
      label: 'Assessment date',
      value: displayDate(episodeInfo?.assessment_date),
    },
    {
      label: 'Episode start date',
      value: displayDate(episodeInfo?.episode_start_date),
    },
    {
      label: 'First billable visit',
      value: displayDate(episodeInfo?.first_billable_visit),
    },
    {
      label: 'Point of origin',
      value: displayValue(admissionInfo?.point_of_origin),
    },
    {
      label: 'Admission type',
      value: displayValue(admissionInfo?.admission_type),
    },
    {
      label: 'Condition codes',
      value:
        admissionInfo?.condition_codes &&
        admissionInfo.condition_codes.length > 0
          ? admissionInfo.condition_codes.join(', ')
          : '-',
      mono: true,
      span2: true,
    },
    {
      label: 'Admission diagnosis code',
      value: displayValue(admissionInfo?.admission_diagnosis_code),
      mono: true,
    },
  ];

  const valueCodes = admissionInfo?.value_codes ?? [];

  const checks: CheckItem[] = billingCycle
    ? (() => {
        const items: CheckItem[] = [];
        // A check the insurance skips passes and says so, the same
        // verdict as the Not Ready row.
        const skips = readinessSkipsOf(billingCycle);

        // The anchor can be history the previous EMR billed. Roger
        // never signed or exported it, so this is not a local pass. Check it
        // before the eligible/missing split below, which would otherwise
        // catch 'external' and fail it.
        if (billingCycle.payment_oasis_status === 'external') {
          items.push({
            key: 'oasis',
            label: 'Payment OASIS',
            status: 'pass',
            message: PAYMENT_OASIS_EXTERNAL_MESSAGE,
            anchor: 'eoe-oasis-visits',
            // A biller billed past the export hold: say who, when and why
            // on the pass, the same note the Ready list's OASIS popover shows.
            note: billingCycle.payment_oasis_hold_override_note ?? undefined,
            noteTone: 'muted',
          });
        } else if (
          billingCycle.payment_oasis_status === 'eligible' ||
          // The skip waives the previous EMR's export hold too, as on the row.
          (billingCycle.payment_oasis_status === 'external_hold' &&
            skips.payment_oasis)
        ) {
          // A payment OASIS the previous EMR performed is gated on status
          // alone: that EMR holds its signature and export.
          const oasisPass = billingCycle.all_oasis_correct_state;
          const done = billingCycle.is_payment_oasis_imported
            ? 'completed'
            : 'signed and exported';
          let oasisMessage = oasisPass
            ? `Payment OASIS is ${done}`
            : `Payment OASIS must be ${done}`;
          if (!oasisPass && skips.payment_oasis) {
            oasisMessage = `Export ${NOT_REQUIRED_MESSAGE.toLowerCase()}`;
          }
          items.push({
            key: 'oasis',
            label: 'Payment OASIS',
            status: oasisPass || skips.payment_oasis ? 'pass' : 'fail',
            message: oasisMessage,
            anchor: 'eoe-oasis-visits',
          });
        } else {
          // Only the payment OASIS must be signed and exported.
          // When none is eligible, this check is the whole story.
          // 'external_hold' lands here too: its reason says what the External
          // EMR export is waiting on and what clears it.
          items.push({
            key: 'payment-oasis',
            label: 'Payment OASIS',
            status: 'fail',
            // Server copy distinguishes "none exists" from "exists but is not
            // signed/exported yet" so the next step is clear.
            message:
              billingCycle.payment_oasis_reason ??
              PAYMENT_OASIS_MISSING_MESSAGE,
            anchor: 'eoe-oasis-visits',
          });
        }

        // HIPPS reports the Create Claim gate
        // (has_computable_hipps_inputs), the same verdict as the Not Ready
        // row. The grouper can still return a code the gate rejects: it reads
        // worksheet diagnoses (cycle, then episode worksheet), the gate the
        // cycle-linked OASIS diagnoses. That code is a note, never
        // a pass. A load error leaves the code unknown, so the check fails.
        const hippsGate = billingCycle.has_computable_hipps_inputs;
        const trimmedHipps = hippsCode?.trim() ?? '';
        const hippsPasses = !error && hippsGate && trimmedHipps !== '';
        let hippsMessage: string;
        if (hippsPasses) {
          hippsMessage = trimmedHipps;
        } else if (error) {
          hippsMessage = hippsGate
            ? 'Claim preview did not load'
            : 'HIPPS inputs are missing';
        } else if (!hippsGate) {
          hippsMessage =
            trimmedHipps !== ''
              ? 'HIPPS inputs are missing'
              : (hippsUnavailableReason ?? 'HIPPS inputs are missing');
        } else {
          hippsMessage =
            hippsUnavailableReason ?? 'HIPPS code could not be generated';
        }
        const hippsItem: CheckItem = {
          key: 'hipps',
          label: 'HIPPS',
          status: hippsPasses ? 'pass' : 'fail',
          message: hippsMessage,
          anchor: 'eoe-diagnoses',
        };
        if (!error && !hippsGate && trimmedHipps !== '') {
          hippsItem.note = `Grouper result ${trimmedHipps}, blocked until inputs are fixed`;
          hippsItem.noteTone = 'muted';
        }
        // Name the submission provenance without changing the verdict.
        // Provenance notes apply only to a passing code.
        if (hippsPasses && hippsSource === 'external_default') {
          hippsItem.note = EXTERNAL_DEFAULT_HIPPS_MESSAGE;
          hippsItem.noteTone = 'warning';
        } else if (hippsPasses && hippsSource === 'external_computed') {
          hippsItem.note = EXTERNAL_COMPUTED_HIPPS_MESSAGE;
          hippsItem.noteTone = 'warning';
        } else if (hippsPasses && hippsSource === 'external_manual') {
          hippsItem.note = EXTERNAL_MANUAL_HIPPS_LABEL;
          hippsItem.noteTone = 'muted';
        }
        items.push(
          skips.hipps
            ? {
                key: 'hipps',
                label: 'HIPPS',
                status: 'pass',
                message: NOT_REQUIRED_MESSAGE,
                anchor: 'eoe-diagnoses',
              }
            : hippsItem
        );

        // Every invalid code on the episode worksheet,
        // principal and secondaries, judged against the ICD-10-CM release for
        // the claim's Through date. The backend sets the severity: invalid
        // codes block, as they block Create Claim. When that
        // release is not loaded the check could not run: a muted warning that
        // does not block, never a silent pass.
        const codeValidity = preview?.diagnosis_code_validity;
        if (codeValidity && !codeValidity.checked) {
          items.push({
            key: 'diagnosis-codes',
            label: 'Diagnosis codes',
            status: 'warn',
            message:
              'Diagnosis codes could not be checked for this Through date',
            mutedMessage: true,
          });
        } else if (codeValidity?.checked) {
          const invalidCodes = codeValidity.invalid_codes ?? [];
          items.push({
            key: 'diagnosis-codes',
            label: 'Diagnosis codes',
            status:
              invalidCodes.length === 0
                ? 'pass'
                : codeValidity.severity === 'block'
                  ? 'fail'
                  : 'warn',
            message:
              invalidCodes.length === 0
                ? 'All diagnosis codes are valid'
                : 'Medicare will return this claim. Fix the codes on the episode worksheet.',
            details: invalidCodes.map(c =>
              invalidDiagnosisCodeClaimMessage(c.code, c.reason)
            ),
          });
        }

        // A cycle with no billable visits fails Visits, the same
        // verdict as the Not Ready row and the Create Claim gate.
        // all_visits_completed alone is vacuously true for such a cycle.
        const visitsReady =
          billingCycle.has_billable_visits &&
          (billingCycle.all_visits_completed || skips.visits);
        items.push({
          key: 'visits',
          label: 'Visits',
          status: visitsReady ? 'pass' : 'fail',
          // Flag on: most visits are billing-ready once QA signs
          // (pending clinician signature). Flag off: visits must be completed.
          message: !billingCycle.has_billable_visits
            ? 'No billable visits in this billing period'
            : skips.visits
              ? NOT_REQUIRED_MESSAGE
              : billingCycle.all_visits_completed
                ? pendingSignatureReady
                  ? 'All billable visits are signed by QA'
                  : 'All billable visits are completed'
                : pendingSignatureReady
                  ? 'Not all billable visits are signed by QA'
                  : 'Not all billable visits are completed',
          anchor: 'eoe-visits',
        });

        // Orders is the one check a biller can change from inside this popup,
        // so once the preview has loaded it is read from the preview, which is
        // refetched after each change. The list row this popup opened from is
        // not refetched, so its all_orders_signed would go stale.
        const allOrdersBillingReady = preview
          ? (preview.orders ?? []).every(o => o.is_billing_ready !== false)
          : billingCycle.all_orders_signed;
        items.push({
          key: 'orders',
          label: 'Orders',
          status: allOrdersBillingReady || skips.orders ? 'pass' : 'fail',
          message: skips.orders
            ? NOT_REQUIRED_MESSAGE
            : allOrdersBillingReady
              ? 'All billing orders are satisfied'
              : 'Not all billing orders are satisfied',
          anchor: 'eoe-orders',
        });

        if (SHOW_F2F_CHECK) {
          items.push({
            key: 'f2f',
            label: 'F2F',
            status: billingCycle.is_f2f_valid ? 'pass' : 'fail',
            message: billingCycle.is_f2f_valid
              ? 'F2F document is valid'
              : 'Document not attached or signed',
          });
        }

        // For Medicare plans the demographics panel shows "Member ID" (from
        // patient_insurance) in place of MBI, so label the check to match the
        // field it scrolls to and avoid a mismatch.
        const idLabel = usesPatientInsuranceId(demographics)
          ? 'Member ID'
          : 'MBI';
        if (!billingCycle.is_mbi_required) {
          items.push({
            key: 'medicare',
            label: idLabel,
            status: 'pass',
            message: `${idLabel} not required for this claim`,
            anchor: 'eoe-mbi',
          });
        } else {
          items.push({
            key: 'medicare',
            label: idLabel,
            status: billingCycle.is_mbi_valid ? 'pass' : 'fail',
            message: billingCycle.is_mbi_valid
              ? `${idLabel} is valid`
              : `${idLabel} is missing`,
            anchor: 'eoe-mbi',
          });
        }

        if (billingCycle.is_pcr_tracking_number_required) {
          items.push({
            key: 'pcr',
            label: 'PCR tracking #',
            status: billingCycle.is_pcr_tracking_number_valid ? 'pass' : 'fail',
            message: billingCycle.is_pcr_tracking_number_valid
              ? 'PCR tracking number is set for this billing period'
              : 'PCR tracking number is missing for this billing period',
          });
        }

        if (billingCycle.is_authorization_required) {
          const authPasses =
            billingCycle.is_authorization_valid || skips.authorization;
          items.push({
            key: 'authorization',
            label: 'Authorization',
            status: authPasses ? 'pass' : 'fail',
            message: skips.authorization
              ? NOT_REQUIRED_MESSAGE
              : billingCycle.is_authorization_valid
                ? 'Authorization on file'
                : 'Authorization issues — see Authorizations',
            anchor: 'eoe-authorizations',
          });
        }

        if (billingCycle.has_missing_invoice_rate === true) {
          items.push({
            key: 'invoice-rate',
            label: 'Invoice rate',
            status: 'fail',
            message: 'A covered visit has no active rate or allowed amount',
          });
        }

        return items;
      })()
    : [];

  const blockingCount = checks.filter(c => c.status === 'fail').length;

  const chartPath =
    billingCycle?.patient_id && billingCycle?.episode_id
      ? patientEpisodePath(billingCycle.patient_id, billingCycle.episode_id)
      : null;

  const patientName = billingCycle?.patient_name || '-';

  const insuranceType = billingCycle?.insurance_type
    ? billingCycle.insurance_type
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
    : null;

  const headerRest = billingCycle
    ? [
        `MRN ${billingCycle.patient_mrn || '-'}`,
        `${displayDate(billingCycle.start_date)} – ${displayDate(
          billingCycle.end_date
        )}`,
        formatMoneyDisplay(billingCycle.total_charges) || '-',
        `HIPPS ${hippsCode?.trim() ? hippsCode : '-'}`,
      ].join(' · ')
    : '';

  const renderField = (field: FieldDef) => (
    <div
      key={field.label}
      data-field-anchor={field.anchor}
      className={field.span2 ? 'col-span-2' : undefined}
    >
      <div className='text-xs text-muted-foreground'>{field.label}</div>
      <div
        className={`text-sm font-medium text-foreground ${
          field.mono ? 'font-mono' : ''
        }`}
      >
        {field.value}
      </div>
    </div>
  );

  const renderFieldSection = (title: string, fields: FieldDef[]) => (
    <section key={title}>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {title}
      </h3>
      <div className='grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4'>
        {fields.map(renderField)}
      </div>
    </section>
  );

  const renderValueCodesSection = () => (
    <section>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Value codes
      </h3>
      {valueCodes.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No value codes on the claim worksheet.
        </p>
      ) : (
        <table className='w-full max-w-md text-sm'>
          <thead>
            <tr className='border-b text-left text-xs text-muted-foreground'>
              <th className='py-1.5 pr-4 font-medium'>Code</th>
              <th className='py-1.5 font-medium'>Amount</th>
            </tr>
          </thead>
          <tbody>
            {valueCodes.map((vc, idx) => (
              <tr
                key={`${vc.code}-${idx}`}
                className='border-b border-border/50'
              >
                <td className='py-1.5 pr-4 font-mono whitespace-nowrap'>
                  {vc.code}
                </td>
                <td className='py-1.5'>{displayValue(vc.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );

  const renderDiagnosesSection = () => {
    const section = preview?.oasis_diagnoses_from_visit;
    return (
      <section data-field-anchor='eoe-diagnoses'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Diagnoses
        </h3>
        {!section ? (
          <p className='text-sm text-muted-foreground'>
            No diagnosis codes on OASIS visits linked to this billing cycle.
          </p>
        ) : (
          <>
            <p className='mb-2 text-xs text-muted-foreground'>
              Latest visit with diagnoses: {section.source_visit_task}
              {section.source_visit_date
                ? ` · ${displayDate(section.source_visit_date)}`
                : null}
            </p>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left text-xs text-muted-foreground'>
                  <th className='py-1.5 pr-4 font-medium'>Role</th>
                  <th className='py-1.5 pr-4 font-medium'>Code</th>
                  <th className='py-1.5 font-medium'>Description</th>
                </tr>
              </thead>
              <tbody>
                {section.diagnoses.map(dx => (
                  <tr
                    key={`${dx.code}-${dx.position}`}
                    className='border-b border-border/50'
                  >
                    <td className='py-1.5 pr-4 whitespace-nowrap'>
                      {dx.is_primary ? (
                        <span className='rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary'>
                          Primary
                        </span>
                      ) : (
                        'Secondary'
                      )}
                    </td>
                    <td className='py-1.5 pr-4 font-mono whitespace-nowrap'>
                      {dx.code}
                    </td>
                    <td className='py-1.5'>{displayValue(dx.description)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    );
  };

  const renderBillableStatusCell = (
    visit: Pick<OasisVisitDetail, 'visit_id' | 'task' | 'billable_status'>
  ) => (
    <td className='py-1.5 pr-4'>
      <Select
        value={visitBillableStatusValue(visit.billable_status)}
        onValueChange={value =>
          void handleBillableStatusChange(
            visit.visit_id,
            value as BillableStatus
          )
        }
        disabled={savingVisitIds.has(visit.visit_id)}
      >
        <SelectTrigger
          selectSize='sm'
          className='w-36'
          aria-label={`Billable status: ${visit.task}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='BILLABLE'>Billable</SelectItem>
          <SelectItem value='NON_BILLABLE'>Non-Billable</SelectItem>
          <SelectItem value='NON_COVERED'>Non-Covered</SelectItem>
        </SelectContent>
      </Select>
    </td>
  );

  const renderOasisVisitsSection = () => {
    const visits = preview?.oasis_visits ?? [];
    return (
      <section data-field-anchor='eoe-oasis-visits'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          OASIS visits
        </h3>
        {visits.length === 0 ? (
          <p className='text-sm text-muted-foreground'>No OASIS visits found</p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Task</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Assigned</th>
                <th className='py-1.5 pr-4 font-medium'>Target date</th>
                <th className='py-1.5 pr-4 font-medium'>Visit date</th>
                <th className='py-1.5 font-medium'>Billable status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visits.map(visit => {
                // The payment OASIS can come from an earlier cycle or
                // the previous episode. Its billable status belongs to that
                // cycle, so it is shown here read-only.
                const inThisCycle =
                  !visit.is_payment_oasis ||
                  visit.billing_cycle_id === billingCycle!.billing_cycle_id;
                return (
                  <tr
                    key={visit.visit_id}
                    className={`border-b border-border/50 ${visit.is_billing_ready === false ? 'bg-red-50' : ''}`}
                  >
                    <td className='py-1.5 pr-4'>
                      {renderVisitTaskLabel(
                        visit.task,
                        visit.is_payment_oasis ? (
                          <Badge
                            variant='outline'
                            className='whitespace-nowrap'
                          >
                            Payment OASIS
                          </Badge>
                        ) : undefined
                      )}
                    </td>
                    <td className='py-1.5 pr-4'>
                      {renderVisitStatusCell(visit.status, visit.is_imported)}
                    </td>
                    <td className='py-1.5 pr-4'>
                      {displayValue(visit.assigned_user)}
                    </td>
                    <td className='py-1.5 pr-4'>
                      {displayDate(visit.target_date)}
                    </td>
                    <td className='py-1.5 pr-4'>
                      {displayDate(visit.visit_date)}
                    </td>
                    {inThisCycle ? (
                      renderBillableStatusCell(visit)
                    ) : (
                      <td className='py-1.5 pr-4 text-muted-foreground'>-</td>
                    )}
                    {renderRowLinkCell(
                      patientVisitPath(
                        billingCycle!.patient_id,
                        visit.visit_id,
                        visit.episode_id ??
                          billingCycle!.episode_id ??
                          undefined
                      ),
                      'View chart'
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderVisitsSection = () => {
    const visitsData = preview?.visits ?? [];
    return (
      <section data-field-anchor='eoe-visits'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Visits
        </h3>
        {visitsData.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No non-OASIS visits found
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Task</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Assigned</th>
                <th className='py-1.5 pr-4 font-medium'>Target date</th>
                <th className='py-1.5 pr-4 font-medium'>Visit date</th>
                <th className='py-1.5 font-medium'>Billable status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visitsData.map(visit => (
                <tr
                  key={`${visit.task}-${visit.visit_date}-${visit.target_date}`}
                  className={`border-b border-border/50 ${visit.is_billing_ready === false ? 'bg-red-50' : ''}`}
                >
                  <td className='py-1.5 pr-4'>
                    {renderVisitTaskLabel(visit.task)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {renderVisitStatusCell(visit.status, visit.is_imported)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.assigned_user)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayDate(visit.target_date)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayDate(visit.visit_date)}
                  </td>
                  {renderBillableStatusCell(visit)}
                  {renderRowLinkCell(
                    patientVisitPath(
                      billingCycle!.patient_id,
                      visit.visit_id,
                      billingCycle!.episode_id ?? undefined
                    ),
                    'View chart'
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderRequiredForBillingCell = (order: EoePreviewOrderItem) => {
    const required = order.required_for_billing !== false;
    const label = `Required for billing: ${order.order_type_display}`;
    // Only users who can open Orders Manager, the other place this can be
    // changed, get an editable switch. This is a UI affordance: the order
    // update endpoint itself does not check the permission.
    if (order.can_edit_required_for_billing && canViewOrders) {
      return (
        <td className='py-1.5'>
          <Switch
            checked={required}
            disabled={savingOrderIds.has(order.order_id)}
            onCheckedChange={checked =>
              void handleRequiredForBillingChange(order.order_id, checked)
            }
            aria-label={label}
          />
        </td>
      );
    }
    // A disabled button fires no pointer events, so the tooltip hangs off a
    // wrapper. The screen-reader copy gives the reason where hover cannot.
    const lockedMessage = order.can_edit_required_for_billing
      ? 'Requires Orders Manager access.'
      : getLockedBillingRequirementMessage(order.order_type_display, required);
    return (
      <td className='py-1.5'>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Switch checked={required} disabled aria-label={label} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{lockedMessage}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className='sr-only'>{lockedMessage}</span>
      </td>
    );
  };

  const renderOrdersSection = () => {
    const ordersData = preview?.orders ?? [];
    return (
      <section data-field-anchor='eoe-orders'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Orders
        </h3>
        {ordersData.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No billing orders found
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Order</th>
                <th className='py-1.5 pr-4 font-medium'>Date</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Physician</th>
                <th className='py-1.5 font-medium'>For billing</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ordersData.map((o, idx) => (
                <tr
                  key={`${o.order_type_display}-${o.order_date}-${idx}`}
                  className={`border-b border-border/50 ${o.is_billing_ready === false ? 'bg-red-50' : ''}`}
                >
                  <td className='py-1.5 pr-4'>{o.order_type_display}</td>
                  <td className='py-1.5 pr-4'>{displayDate(o.order_date)}</td>
                  <td className='py-1.5 pr-4'>{renderStatusBadge(o.status)}</td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(o.physician_name)}
                  </td>
                  {renderRequiredForBillingCell(o)}
                  {canViewOrders ? (
                    renderRowLinkCell(
                      billingOrderPath({
                        orderId: o.order_id,
                        status: o.status,
                        patientId: billingCycle!.patient_id,
                        episodeId: billingCycle!.episode_id,
                      }),
                      'View order'
                    )
                  ) : (
                    <td className='py-1.5 pl-2 text-right' />
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderAuthorizationsSection = () => {
    if (!billingCycle?.is_authorization_required) return null;

    const isValid = billingCycle.is_authorization_valid;

    return (
      <section data-field-anchor='eoe-authorizations'>
        <div className='mb-2 flex items-center gap-2'>
          <h3 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            Authorizations
          </h3>
          {isValid ? (
            <CheckCircle2 className='h-3.5 w-3.5 text-green-600' />
          ) : (
            <XCircle className='h-3.5 w-3.5 text-red-600' />
          )}
        </div>
        {authLoadFailed ? (
          <p className='text-sm text-muted-foreground'>
            Unable to load authorizations
          </p>
        ) : authData === null ? (
          <p className='text-sm text-muted-foreground'>
            Loading authorizations…
          </p>
        ) : authData.auths.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No authorizations on file.
          </p>
        ) : (
          <div className='space-y-2'>
            {authData.auths.map(auth => (
              <div
                key={auth.id}
                className='rounded-md border border-border/60 px-3 py-2 text-sm'
              >
                <div className='flex items-center gap-3'>
                  <span className='font-medium'>
                    {auth.authorization_number
                      ? `#${auth.authorization_number}`
                      : '—'}
                  </span>
                  {auth.status && (
                    <Badge
                      variant={getStatusBadgeVariant(auth.status as never)}
                    >
                      {auth.status}
                    </Badge>
                  )}
                  <span className='text-muted-foreground'>
                    {formatDateToMMDDYYYY(auth.start_date)} –{' '}
                    {formatDateToMMDDYYYY(auth.end_date)}
                  </span>
                </div>
                <div className='mt-1 flex flex-wrap gap-3'>
                  {AUTH_DISCIPLINE_FIELDS.map(f => {
                    const allowed = auth[f.key] as number | null | undefined;
                    if (allowed == null) return null;
                    // Readiness ignores DENIED/CANCELLED auths, so don't show
                    // used/over-budget styling for inactive ones — just the budget.
                    const isActive =
                      auth.status !== 'DENIED' && auth.status !== 'CANCELLED';
                    if (!isActive) {
                      return (
                        <span
                          key={f.key}
                          className='text-xs text-muted-foreground'
                        >
                          {f.label}: {allowed}
                        </span>
                      );
                    }
                    // Used count comes from the same server rule the error
                    // list below is built from (consumes_authorization_slot),
                    // so the two never disagree.
                    const usedCount =
                      authData.budget
                        .find(b => b.authorization_id === auth.id)
                        ?.disciplines.find(d => d.discipline === f.label)
                        ?.used ?? 0;
                    const isOver = usedCount > allowed;
                    return (
                      <span
                        key={f.key}
                        className={`text-xs ${isOver ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
                      >
                        {f.label}: {usedCount}/{allowed}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {authData !== null &&
          authData.auths.length > 0 &&
          authData.errors.length > 0 && (
            <ul className='mt-2 space-y-1'>
              {authData.errors.map((err, idx) => (
                <li key={idx} className='text-xs text-red-700'>
                  {err.message}
                </li>
              ))}
            </ul>
          )}
        <button
          type='button'
          onClick={navigateAndClose(
            patientAuthorizationsPath(
              billingCycle.patient_id,
              billingCycle.episode_id
            )
          )}
          className='mt-2 text-xs font-medium text-primary underline-offset-2 hover:underline'
        >
          View authorizations ↗
        </button>
      </section>
    );
  };

  const renderRail = () => (
    <aside className='min-h-0 overflow-y-auto border-r bg-muted/40 px-4 py-4'>
      <div
        className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${
          blockingCount > 0
            ? 'border-red-200 bg-red-50 text-red-800'
            : 'border-green-200 bg-green-50 text-green-800'
        }`}
      >
        {blockingCount > 0 ? (
          <>
            <XCircle className='h-4 w-4 shrink-0 text-red-600' />
            <span>
              Not ready
              <span className='font-normal'> · {blockingCount} blocking</span>
            </span>
          </>
        ) : (
          <>
            <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
            <span>Ready to bill</span>
          </>
        )}
      </div>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Readiness checks
      </h3>
      <ul className='space-y-2'>
        {checks.map(check => {
          const isClickable =
            check.status === 'fail' && !!check.anchor && !!preview;

          const body = (
            <>
              <div className='flex items-baseline gap-2'>
                {check.status === 'pass' ? (
                  <CheckCircle2
                    aria-label='Passing'
                    className='h-3.5 w-3.5 shrink-0 self-center text-green-600'
                  />
                ) : check.status === 'warn' ? (
                  <AlertTriangle
                    aria-label='Warning'
                    className='h-3.5 w-3.5 shrink-0 self-center text-amber-600'
                  />
                ) : (
                  <XCircle
                    aria-label='Failing'
                    className='h-3.5 w-3.5 shrink-0 self-center text-red-600'
                  />
                )}
                <span className='text-sm font-medium text-foreground'>
                  {check.label}
                </span>
              </div>
              <p
                className={`ml-[1.375rem] text-xs leading-snug ${
                  check.status === 'fail'
                    ? 'text-red-700'
                    : check.status === 'warn' && !check.mutedMessage
                      ? 'text-amber-700'
                      : 'text-muted-foreground'
                } ${
                  check.key === 'hipps' && check.status === 'pass'
                    ? 'font-mono'
                    : ''
                }`}
              >
                {check.message}
                {isClickable ? (
                  <span className='ml-1 whitespace-nowrap font-medium underline underline-offset-2'>
                    View ↓
                  </span>
                ) : null}
              </p>
              {check.details && check.details.length > 0 ? (
                <ul
                  className={`mt-0.5 ml-[1.375rem] space-y-0.5 text-xs leading-snug ${
                    check.status === 'fail' ? 'text-red-700' : 'text-amber-700'
                  }`}
                >
                  {check.details.map(detail => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : null}
              {check.note ? (
                <p
                  className={`mt-0.5 ml-[1.375rem] text-xs leading-snug ${
                    check.noteTone === 'warning'
                      ? 'text-amber-700'
                      : 'text-muted-foreground'
                  }`}
                >
                  {check.note}
                </p>
              ) : null}
            </>
          );

          return (
            <li key={check.key}>
              {isClickable ? (
                <button
                  type='button'
                  onClick={() =>
                    check.anchor &&
                    scrollToField(check.anchor, { focus: false })
                  }
                  className='w-full rounded-md text-left transition-colors hover:bg-red-50'
                  aria-label={`${check.label}: ${check.message}. View details.`}
                >
                  {body}
                </button>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );

  return (
    <Dialog open={billingCycleProp !== null} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0'>
        <DialogHeader className='border-b px-6 py-4'>
          <DialogTitle>EOE Claim Preview</DialogTitle>
          <DialogDescription className='sr-only'>
            Preview the end-of-episode claim readiness checks and claim details.
          </DialogDescription>
          {billingCycle ? (
            <>
              <p className='text-sm font-medium text-foreground'>
                {billingCycle.insurance_name || '-'}
                {insuranceType ? (
                  <span className='ml-2 font-normal text-muted-foreground'>
                    · {insuranceType}
                  </span>
                ) : null}
              </p>
              <p className='text-sm text-muted-foreground'>
                {chartPath ? (
                  <Link
                    to={chartPath}
                    className='font-medium text-primary underline-offset-4 hover:underline'
                  >
                    {patientName}
                  </Link>
                ) : (
                  patientName
                )}
                {` · ${headerRest}`}
                {chartPath ? (
                  <>
                    {' · '}
                    <Link
                      to={chartPath}
                      className='font-medium text-primary underline-offset-4 hover:underline'
                    >
                      View episode <span aria-hidden='true'>↗</span>
                    </Link>
                  </>
                ) : null}
              </p>
            </>
          ) : null}
        </DialogHeader>

        {isLoading ? (
          <div className='flex flex-1 flex-col items-center justify-center py-10 text-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <p className='mt-2 text-sm'>Loading billing cycle details...</p>
          </div>
        ) : (
          <div className='grid flex-1 min-h-0 grid-cols-1 lg:grid-cols-[14rem_1fr]'>
            {billingCycle ? renderRail() : null}
            <main className='min-h-0 overflow-y-auto px-6 py-4'>
              {readinessScope && billingCycle ? (
                <section
                  data-testid='draft-readiness-corrections'
                  className='mb-4'
                >
                  <ClaimReadinessDetails
                    entry={billingCycle}
                    scope={readinessScope}
                    fieldOnly
                    onHoldOverridden={async () => {
                      const id = billingCycle.billing_cycle_id;
                      await Promise.all([
                        refreshPreview(id),
                        refreshReadiness(id),
                      ]);
                    }}
                  />
                </section>
              ) : null}
              {error ? (
                <Alert className='border-red-200 bg-red-50'>
                  <AlertCircle className='h-4 w-4 text-red-600' />
                  <AlertDescription className='text-red-800'>
                    <p>
                      An error happened while loading billing cycle details.
                      Please try again and contact support if the issue happens
                      again.
                    </p>
                    <p className='mt-2'>{error}</p>
                  </AlertDescription>
                </Alert>
              ) : billingCycle && preview ? (
                <div className='space-y-5'>
                  {renderFieldSection('Patient', patientFields)}
                  {renderFieldSection('Physician', physicianFields)}
                  {renderFieldSection(
                    'Episode & Admission',
                    episodeAdmissionFields
                  )}
                  {renderValueCodesSection()}
                  {renderDiagnosesSection()}
                  {renderOasisVisitsSection()}
                  {renderVisitsSection()}
                  {renderOrdersSection()}
                  {renderAuthorizationsSection()}
                </div>
              ) : (
                <div className='flex min-h-[12rem] flex-col items-center justify-center text-center'>
                  <AlertCircle className='h-5 w-5 text-muted-foreground' />
                  <p className='mt-2 text-sm text-muted-foreground'>
                    No claim details available for this billing cycle.
                  </p>
                </div>
              )}
            </main>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EOEPreviewPopup;
